import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import Link from 'next/link'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  // Base styles with proper focus states for accessibility
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md",
    "text-sm font-medium transition-all duration-200",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0",
    // Focus states - visible ring for keyboard navigation (WCAG compliant)
    "outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
  ].join(' '),
  {
    variants: {
      variant: {
        // Primary CTA - main action buttons
        default: [
          'bg-primary text-white',
          'hover:bg-[var(--brand-primary-dark)]',
          'shadow-sm hover:shadow-md active:scale-[0.98]',
          'focus-visible:ring-primary',
        ].join(' '),
        // Primary alias
        primary: [
          'bg-primary text-white',
          'hover:bg-[var(--brand-primary-dark)]',
          'shadow-sm hover:shadow-md active:scale-[0.98]',
          'focus-visible:ring-primary',
        ].join(' '),
        // Secondary CTA - matches red pill styling (same as SectionBadge)
        secondary: [
          'text-primary bg-primary/10',
          'ring-1 ring-primary/10',
          'hover:bg-primary/20',
          'focus-visible:ring-primary',
        ].join(' '),
        // Outline variant
        outline: [
          'border border-border bg-background',
          'shadow-xs hover:bg-accent hover:text-accent-foreground',
          'focus-visible:ring-ring',
        ].join(' '),
        // Destructive actions
        destructive: [
          'bg-destructive text-white',
          'hover:bg-destructive/90',
          'focus-visible:ring-destructive',
        ].join(' '),
        // Ghost - minimal styling
        ghost: [
          'hover:bg-accent hover:text-accent-foreground',
          'focus-visible:ring-ring',
        ].join(' '),
        // Link style
        link: [
          'text-primary underline-offset-4 hover:underline',
          'focus-visible:ring-primary',
        ].join(' '),
      },
      size: {
        default: 'h-10 px-5 py-2',
        sm: 'h-9 rounded-md gap-1.5 px-3',
        md: 'h-10 px-5 py-2',
        lg: 'h-11 rounded-md px-8',
        // Ensure minimum touch target of 44x44px on mobile
        icon: 'size-11 min-w-[44px] min-h-[44px]',
        'icon-sm': 'size-10 min-w-[44px] min-h-[44px]',
        'icon-lg': 'size-12 min-w-[44px] min-h-[44px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  href?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, href, children, ...props }, ref) => {
    // If href is provided, render as Next.js Link
    if (href) {
      return (
        <Link
          href={href}
          className={cn(buttonVariants({ variant, size, className }))}
        >
          {children}
        </Link>
      )
    }

    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
export default Button
